import * as React from 'react';
import { useFloating, useClick, useDismiss, useRole, useInteractions, FloatingFocusManager, useTransitionStatus, FloatingPortal } from '@floating-ui/react';
import * as Styled from './Modal.styled';
import { Close } from '../../svgs';

/**
 * This is for wiring up the reference/trigger element to open the moal.
 * Note, the Floating UI docs show setting a "ref" prop as well. In our case, it
 * is not needed because we don't do any direct DOM manipulation on the trigger
 * element.
 *
 * If in the future we need it, the type is
 * { ref: ReturnType<typeof useFloating>['refs']['setReference'] }
 */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const Modal = ({
  children,
  closeButtonLabel,
  isOpen,
  setIsOpen,
  renderTrigger,
  ariaLabelledById,
  initialFocus
}) => {
  const {
    refs,
    context
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen
  });
  const role = useRole(context);
  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown'
  });
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([click, dismiss, role]);
  const {
    status
  } = useTransitionStatus(context);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderTrigger({
      ...getReferenceProps()
    }), /*#__PURE__*/_jsx(FloatingPortal, {
      children: isOpen && /*#__PURE__*/_jsx(Styled.ModalOverlay, {
        lockScroll: true,
        children: /*#__PURE__*/_jsx(FloatingFocusManager, {
          order: ['floating', 'content'],
          context: context,
          initialFocus: initialFocus,
          children: /*#__PURE__*/_jsx(Styled.SlideAnimationContainer, {
            children: /*#__PURE__*/_jsxs(Styled.ModalContainer, {
              ref: refs.setFloating,
              "aria-labelledby": ariaLabelledById ?? undefined,
              ...getFloatingProps(),
              "data-transition-status": status,
              children: [children, closeButtonLabel && /*#__PURE__*/_jsx(Styled.ModalCloseButton, {
                sizePreset: "sm",
                onClick: () => setIsOpen(false),
                "aria-label": closeButtonLabel,
                children: /*#__PURE__*/_jsx(Close, {})
              })]
            })
          })
        })
      })
    })]
  });
};
Modal.Header = Styled.ModalHeader;
Modal.Footer = Styled.ModalFooter;
Modal.Title = Styled.ModalTitle;
Modal.Content = Styled.ModalContent;
Modal.CtaButtonGroup = Styled.CtaButtonGroup;